import type { AuthedAxiosRequests } from 'features/auth'
import { isMissingAccessError } from 'features/permissions/utils/isMissingAccessError'
import {
  type ActiveScope,
  ROOT_SCOPE_NAME,
  ROOT_SCOPE_TYPE,
} from 'features/permissions/utils/policyScopeEvaluation'
import {
  type RolodexV3ScopedRole,
  v3ScopedRolesFromResponse,
} from 'features/permissions/utils/v3RoleNames'
import { ROLE_CANTILEVER_DOCUMENT_NAVIGATOR_VIEWER } from 'types'

export interface RolodexV3RolesAtScope {
  /** Roles granted at the active site, its organization, or root. */
  roles: readonly RolodexV3ScopedRole[]
  /** Whether a Document Navigator viewer grant exists at any scope the user can read. */
  viewerAssignedAnywhere: boolean
}

export const NO_V3_ROLES: RolodexV3RolesAtScope = {
  roles: [],
  viewerAssignedAnywhere: false,
}

/**
 * One `scope` query per level. Rolodex rejects the whole request with 403 when
 * any listed scope is outside the caller's read access, so they can't be combined.
 */
function scopeQueries(active: ActiveScope): string[] {
  const queries = [`${ROOT_SCOPE_TYPE}:${ROOT_SCOPE_NAME}`]
  if (active.kind !== 'root' && active.orgId) {
    queries.push(`organization:${active.orgId}`)
  }
  if (active.kind === 'site') {
    queries.push(`site:${active.siteId}`)
  }
  return queries
}

async function getUserRoles(
  client: AuthedAxiosRequests,
  url: string,
  params: { scope: string } | { name: string }
): Promise<RolodexV3ScopedRole[]> {
  try {
    const response = await client.get(url, { params })
    return v3ScopedRolesFromResponse(response.data)
  } catch (error) {
    if (isMissingAccessError(error)) return []
    throw error
  }
}

/**
 * Roles for the active scope from filtered GET /users/{id}/roles calls, plus
 * a name-filtered call for the viewer grant, which the leftover upload mask
 * needs at every site.
 */
export async function fetchRolodexV3RolesAtScope(
  client: AuthedAxiosRequests,
  rolodexUrl: string,
  userId: string,
  active: ActiveScope
): Promise<RolodexV3RolesAtScope> {
  const url = `${rolodexUrl}/api/v3/users/${encodeURIComponent(userId)}/roles`
  const [viewerRoles, ...scopedLists] = await Promise.all([
    getUserRoles(client, url, {
      name: ROLE_CANTILEVER_DOCUMENT_NAVIGATOR_VIEWER,
    }),
    ...scopeQueries(active).map((scope) =>
      getUserRoles(client, url, { scope })
    ),
  ])
  return {
    roles: scopedLists.flat(),
    viewerAssignedAnywhere: viewerRoles.length > 0,
  }
}
